<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { CsIcon } from '../icon';
import { toastRecipe } from './toast.recipe';

/**
 * @typedef {object} ToastItemProps
 * @property {object} toast - The toast object to display
 * @property {Array} toasts - Array of all toast objects
 * @property {number} index - Index of this toast in the toasts array
 * @property {boolean} expanded - Whether the toast stack is expanded
 * @property {Array} heights - Array of toast height objects
 * @property {('top-center'|'top-right'|'top-left'|'bottom-center'|'bottom-right'|'bottom-left')} [position='top-center'] - Position of the toast
 * @property {number} [visibleToasts=6] - Maximum number of visible toasts
 * @property {boolean} [stackByDefault=true] - Whether toasts should be stacked by default
 * @property {boolean} interacting - Whether user is currently interacting with toasts
 */

/**
 * @type {ToastItemProps}
 */
const props = defineProps({
  toast: {
    type: Object,
    required: true,
  },
  toasts: {
    type: Array,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  expanded: {
    type: Boolean,
    required: true,
  },
  heights: {
    type: Array,
    required: true,
  },
  position: {
    type: String,
    default: 'top-center',
  },
  visibleToasts: {
    type: Number,
    required: false,
    default: 6,
  },
  stackByDefault: {
    type: Boolean,
    required: false,
    default: true,
  },
  interacting: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['update-height', 'remove']);

// Default gap between toasts
const GAP = 14;

const TIME_BEFORE_UNMOUNT = 400;

const mounted = ref(false);
const removed = ref(false);
const offsetBeforeRemove = ref(0);
const initialHeight = ref(0);
const toastRef = ref(null);
const isFront = computed(() => props.index === 0);
const isVisible = computed(() => props.index + 1 <= props.visibleToasts);
const timeoutId = ref(null);

// Height index is used to calculate the offset as it gets updated before the toast array, which means we can calculate the new layout faster.
const heightIndex = computed(
  () =>
    props.heights.findIndex(height => height.toastId === props.toast.id) || 0
);
const duration = computed(() => props.toast.duration);

const closeTimerStartTimeRef = ref(0);
const offset = ref(0);
const closeTimerRemainingTimeRef = ref(duration.value);
const lastCloseTimerStartTimeRef = ref(0);

const toastsHeightBefore = computed(() => {
  return props.heights.reduce((prev, curr, reducerIndex) => {
    // Calculate offset up untill current  toast
    if (reducerIndex >= heightIndex.value) return prev;

    return prev + curr.height;
  }, 0);
});

onMounted(() => (mounted.value = true));

// Replace watchEffect with a computed property
const calculatedOffset = computed(() => {
  return heightIndex.value * GAP + toastsHeightBefore.value;
});

// Use a watcher to update offset when calculatedOffset changes
watch(
  calculatedOffset,
  newValue => {
    offset.value = newValue;
  },
  { immediate: true }
);

function closeToast() {
  // Save the offset for the exit swipe animation
  removed.value = true;
  offsetBeforeRemove.value = offset.value;

  // Don't immediately update heights - this causes position jumps
  // Instead, wait for the animation to complete before updating heights
  setTimeout(() => {
    const newHeights = props.heights.filter(
      height => height.toastId !== props.toast.id
    );
    // eslint-disable-next-line vue/custom-event-name-casing
    emit('update-height', newHeights);
  }, TIME_BEFORE_UNMOUNT / 2);

  setTimeout(() => {
    emit('remove', props.toast);
  }, TIME_BEFORE_UNMOUNT);
}

// Function to pause the timer
function pauseTimer() {
  if (lastCloseTimerStartTimeRef.value < closeTimerStartTimeRef.value) {
    // Get the elapsed time since the timer started
    const elapsedTime = new Date().getTime() - closeTimerStartTimeRef.value;
    closeTimerRemainingTimeRef.value -= elapsedTime;
  }
  lastCloseTimerStartTimeRef.value = new Date().getTime();

  // Clear any existing timeout
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
}

// Function to start the timer
function startTimer() {
  closeTimerStartTimeRef.value = new Date().getTime();
  // Clear any existing timeout first
  if (timeoutId.value) clearTimeout(timeoutId.value);

  // Let the toast know it has started
  timeoutId.value = setTimeout(() => {
    props.toast.onAutoClose?.(props.toast);
    closeToast();
  }, closeTimerRemainingTimeRef.value);
}

// Handle timer based on expanded/interacting state
function updateTimerState() {
  if (
    props.toast.promise ||
    props.toast.duration === Number.POSITIVE_INFINITY
  ) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
    return;
  }

  if (props.expanded || props.interacting) {
    pauseTimer();
  } else {
    startTimer();
  }
}

function handleClickAction() {
  props.toast.onClickAction?.();
  closeToast();
}

// Watch for changes in expanded or interacting props
watch(
  [() => props.expanded, () => props.interacting],
  () => {
    updateTimerState();
  },
  { immediate: true }
);

// Watch for changes in toast promise or duration
watch(
  [() => props.toast.promise, () => props.toast.duration],
  () => {
    updateTimerState();
  },
  { immediate: true }
);

onMounted(() => {
  if (toastRef.value) {
    const height = toastRef.value.getBoundingClientRect().height;
    // Add toast height tot heights array after the toast is mounted
    initialHeight.value = height;

    const newHeights = [{ toastId: props.toast.id, height }, ...props.heights];
    // eslint-disable-next-line vue/custom-event-name-casing
    emit('update-height', newHeights);
  }
});

onUnmounted(() => {
  if (toastRef.value) {
    const newHeights = props.heights.filter(
      height => height.toastId !== props.toast.id
    );
    // eslint-disable-next-line vue/custom-event-name-casing
    emit('update-height', newHeights);
  }

  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
});

watch(
  () => props.toast.delete,
  val => {
    if (val) closeToast();
  },
  { deep: true }
);
</script>

<template>
  <div
    ref="toastRef"
    data-component="CsToastItem"
    :class="toastRecipe().root({ type: props.toast.type })"
    tabindex="0"
    :data-mounted="mounted ? 'true' : 'false'"
    :data-removed="removed ? 'true' : 'false'"
    :data-visible="isVisible ? 'true' : 'false'"
    :data-index="props.index"
    :data-front="isFront ? 'true' : 'false'"
    :data-expanded="
      props.expanded || (props.stackByDefault && mounted) ? 'true' : 'false'
    "
    :style="{
      '--index': props.index,
      '--toasts-before': props.index,
      '--z-index': props.toasts.length - props.index,
      '--offset': `${removed ? offsetBeforeRemove : offset}px`,
      '--initial-height': props.stackByDefault ? 'auto' : `${initialHeight}px`,
    }"
  >
    <span>
      {{ props.toast.content }}
    </span>

    <span
      v-if="props.toast.actionLabel || props.toast.isShowCloseIcon"
      :class="toastRecipe().actionContainer({ type: props.toast.type })"
    >
      <button
        v-if="props.toast.actionLabel"
        :class="toastRecipe().action({ type: props.toast.type })"
        @click="handleClickAction"
      >
        {{ props.toast.actionLabel }}
      </button>

      <button
        v-if="props.toast.isShowCloseIcon"
        :class="toastRecipe().closeButton({ type: props.toast.type })"
        @click="closeToast"
      >
        <CsIcon name="x" />
      </button>
    </span>
  </div>
</template>

<style>
[data-component='CsToastItem'] {
  --y: translateY(100%);
  --lift-amount: calc(var(--lift) * var(--gap));
  z-index: var(--z-index);
  opacity: 0;
  position: absolute;
  transform: var(--y);
  touch-action: none;
  will-change: transform, opacity, height;
  transition:
    transform 600ms,
    opacity 400ms,
    height 400ms,
    box-shadow 600ms;
  box-sizing: border-box;
  outline: none;
  overflow-wrap: anywhere;
}

[data-component='CsToastItem'] {
  top: 0;
  --y: translateY(-100%);
  --lift: 1;
  --lift-amount: calc(1 * var(--gap));
  --scale: 0.9;
}

[data-component='CsToastItem'][data-mounted='true'] {
  --y: translateY(0);
  opacity: 1;
  transition-timing-function: cubic-bezier(0.19, 0.91, 0.38, 1);
}

[data-component='CsToastItem'][data-expanded='false'][data-front='false'] {
  --scale: var(--toasts-before) * 0.05 + 1;
  --y: translateY(calc(var(--lift-amount) * var(--toasts-before)))
    scale(calc(-1 * var(--scale)));
  height: var(--front-toast-height);
}

[data-component='CsToastItem'][data-expanded='false'][data-front='false'] > * {
  opacity: 0;
}

[data-component='CsToastItem'][data-visible='false'] {
  opacity: 0;
  pointer-events: none;
}

[data-component='CsToastItem'][data-mounted='true'][data-expanded='true'] {
  --y: translateY(calc(var(--lift) * var(--offset)));
  height: var(--initial-height);
}

[data-component='CsToastItem'][data-removed='true'][data-front='true'] {
  --y: 0;
  opacity: 0;
  transform: translateY(0) scale(0.9);
  transition-timing-function: cubic-bezier(0.42, 0, 1, 1);
}

[data-component='CsToastItem'][data-removed='true'][data-front='false'][data-expanded='true'] {
  --y: translateY(calc(var(--lift) * var(--offset) + var(--lift) * -100%));
  opacity: 0;
  transform: var(--y) scale(0.9);
  transition-timing-function: cubic-bezier(0.42, 0, 1, 1);
}

[data-component='CsToastItem'][data-removed='true'][data-front='false'][data-expanded='false'] {
  --y: translateY(40%);
  opacity: 0;
  transition:
    transform 500ms,
    opacity 200ms;
}
</style>
